import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Spinner,
  Center,
  useToast,
  Badge,
  Text,
  Flex,
  Select,
  VStack,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import { supabase } from "../../../lib/supabaseClient";

const PartnersListMonthly = () => {
  const toast = useToast();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [monthlyData, setMonthlyData] = useState({});
  const [summary, setSummary] = useState({
    totalSum: 0,
    totalCalculated: 0,
    payoutAmount: 0,
    netAmount: 0,
  });
  const [selectedContractor, setSelectedContractor] = useState(
    "allthebiz00@gmail.com"
  );

  // 현재 월부터 2024년 4월까지의 월 목록 생성
  const monthOptions = useMemo(() => {
    const months = [];
    const currentDate = new Date();
    const startDate = new Date(2024, 3, 1); // 2024년 4월

    while (currentDate >= startDate) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, "0");
      months.push(`${year}${month}`);
      currentDate.setMonth(currentDate.getMonth() - 1);
    }
    return months;
  }, []); // 의존성 배열이 비어있어서 한 번만 실행

  useEffect(() => {
    if (monthOptions.length > 0) {
      setSelectedMonth(monthOptions[0]);
    }
  }, [monthOptions]);

  useEffect(() => {
    if (selectedMonth) {
      fetchPartnersData();
    }
  }, [selectedMonth, selectedContractor]);

  const fetchPartnersData = async () => {
    try {
      setLoading(true);

      // 1. shorts_contract 테이블에서 파트너 정보 가져오기
      const { data: contracts, error: contractsError } = await supabase
        .from("shorts_contract")
        .select("partner, commission, email, contractor_name")
        .eq("partner", selectedContractor);

      if (contractsError) throw contractsError;

      // 2. payout_partner 테이블에서 지급 금액 합계 가져오기
      const { data: payoutData, error: payoutError } = await supabase
        .from("payout_partner")
        .select("amount")
        .eq("partner_user", selectedContractor);

      if (payoutError) {
        console.error("지급 데이터 조회 실패:", payoutError);
      }

      const payoutAmount =
        payoutData?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;

      // 3. 각 파트너별로 월별 정산 데이터 가져오기 (get_total_sum_with_commission_month 함수 사용)
      const partnersData = [];
      let totalSum = 0;
      let totalCalculated = 0;

      for (const contract of contracts) {
        const { data: settlementData, error: settlementError } =
          await supabase.rpc("get_total_sum_with_commission_month", {
            target_email: contract.email,
            target_month: selectedMonth,
          });

        if (settlementError) {
          console.error(
            `Settlement error for ${contract.email}:`,
            settlementError
          );
          continue;
        }

        const settlement = settlementData?.[0];
        if (settlement) {
          partnersData.push({
            ...contract,
            total_sum: settlement.total_sum || 0,
            calculated_value: settlement.calculate_sum || 0,
          });

          totalSum += settlement.total_sum || 0;
          totalCalculated += settlement.calculate_sum || 0;
        }
      }

      // 순수익 계산: 총 계산 금액 - 지급 금액
      const netAmount = totalCalculated - payoutAmount;

      setPartners(partnersData);
      setSummary({ totalSum, totalCalculated, payoutAmount, netAmount });
      setMonthlyData({ [selectedMonth]: partnersData });
    } catch (error) {
      console.error("파트너스 데이터 조회 실패:", error);
      toast({
        title: "파트너스 데이터를 불러오는데 실패했습니다",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ko-KR").format(Math.round(amount));
  };

  const handleContractorChange = (contractor) => {
    setSelectedContractor(contractor);
  };

  if (loading) {
    return (
      <Center minH="60vh">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" p={6}>
      <Container maxW="container.xl">
        <VStack spacing={6} align="stretch">
          {/* 헤더 */}
          <Flex justify="space-between" align="center">
            <Heading size="lg" color="gray.800">
              파트너스 관리 (월별)
            </Heading>
            <HStack spacing={4}>
              {/* 계약자 선택 */}
              <Select
                value={selectedContractor}
                onChange={(e) => handleContractorChange(e.target.value)}
                w="300px"
                bg="white"
              >
                <option value="allthebiz00@gmail.com">
                  allthebiz00@gmail.com
                </option>
                <option value="m-c@musiccolla.com">m-c@musiccolla.com</option>
                <option value="bbangsik007@naver.com">
                  bbangsik007@naver.com
                </option>
                <option value="have-company@naver.com">
                  have-company@naver.com
                </option>
              </Select>
              {/* 월 선택 */}
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                w="200px"
                bg="white"
              >
                {monthOptions.map((month) => (
                  <option key={month} value={month}>
                    {month.substring(0, 4)}년 {month.substring(4)}월
                  </option>
                ))}
              </Select>
            </HStack>
          </Flex>

          {/* 요약 정보 */}
          <Card>
            <CardBody>
              <HStack spacing={6} justify="center">
                <Stat textAlign="center">
                  <StatLabel>총 정산 금액</StatLabel>
                  <StatNumber color="blue.600">
                    {formatCurrency(summary.totalSum)}원
                  </StatNumber>
                  <StatHelpText>선택된 월 기준</StatHelpText>
                </Stat>
                <Stat textAlign="center">
                  <StatLabel>월 파트너스 수익 합계</StatLabel>
                  <StatNumber color="green.600">
                    {formatCurrency(summary.totalCalculated)}원
                  </StatNumber>
                  <StatHelpText>
                  레이블리 수익의 30%를 계산한 금액
                  </StatHelpText>
                </Stat>
                <Stat textAlign="center">
                  <StatLabel>총 지급 금액</StatLabel>
                  <StatNumber color="orange.600">
                    {formatCurrency(summary.payoutAmount)}원
                  </StatNumber>
                  <StatHelpText>지급 금액 합계</StatHelpText>
                </Stat>
              </HStack>
            </CardBody>
          </Card>

          {/* 파트너스 테이블 */}
          <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>파트너</Th>
                  <Th>계약자</Th>
                  <Th>계약자명</Th>
                  <Th>수수료율(%)</Th>
                  <Th>정산 금액(원)</Th>
                  <Th>계산 금액(원)</Th>
                </Tr>
              </Thead>
              <Tbody>
                {partners.length === 0 ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8}>
                      <Text color="gray.500">
                        해당 월의 파트너스 데이터가 없습니다
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  partners.map((partner, index) => (
                    <Tr key={index} _hover={{ bg: "gray.50" }}>
                      <Td>
                        <Text fontWeight="medium">{partner.partner}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {partner.email}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {partner.contractor_name || "-"}
                        </Text>
                      </Td>
                      <Td>
                        <Badge colorScheme="blue" variant="subtle">
                          {partner.commission || 10}%
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontWeight="medium" color="blue.600">
                          {formatCurrency(partner.total_sum)}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontWeight="medium" color="green.600">
                          {formatCurrency(partner.calculated_value)}
                        </Text>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default PartnersListMonthly;
